import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import styles from './ToolPassportModal.module.css';
import { DEFAULT_LOGO } from '../../constants/homeConstants';

/**
 * Tool Passport Modal Component
 * Displays detailed information about a tool passport in a modal dialog
 *
 * @param {object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to close the modal
 * @param {object} props.toolPassport - Tool passport data object
 */
const ToolPassportModal = ({ open, onClose, toolPassport }) => {
  const { t } = useTranslation();

  if (!toolPassport) return null;

  /**
   * Sanitize HTML content to prevent XSS attacks
   * @param {string} html - HTML content to sanitize
   * @returns {object} Sanitized HTML object for dangerouslySetInnerHTML
   */
  const sanitizeHTML = (html) => {
    if (!html) return { __html: '' };
    return { __html: DOMPurify.sanitize(html) };
  };

  /**
   * Get logo URL with fallback
   */
  const getLogoUrl = () => {
    if (toolPassport.logo) {
      return `${process.env.REACT_APP_SERVER_URLForLogo}/${toolPassport.logo}`;
    }
    return DEFAULT_LOGO;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      className={styles.dialog}
    >
      <DialogTitle className={styles.dialogTitle}>
        <span>{t("toolPassport")}</span>
        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          aria-label={t("Close")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <table className={styles.detailsTable}>
          <tbody>
            {/* Tool Name */}
            <tr>
              <td className={styles.labelCell}>
                <strong>{t("toolPassportToolName")}</strong>
              </td>
              <td className={styles.valueCell}>
                {toolPassport.name}
              </td>
            </tr>

            {/* Logo */}
            <tr>
              <td className={styles.labelCell}>
                <strong>{t("toolPassportLogo")}</strong>
              </td>
              <td className={styles.valueCell}>
                <img
                  src={getLogoUrl()}
                  alt={`${toolPassport.name} logo`}
                  className={styles.toolLogo}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DEFAULT_LOGO;
                  }}
                />
              </td>
            </tr>

            {/* Type of Tool */}
            <tr>
              <td className={styles.labelCell}>
                <strong>{t("typeOfTool")}</strong>
              </td>
              <td className={styles.valueCell}>
                {toolPassport.typeOfToolName || '-'}
              </td>
            </tr>

            {/* Category */}
            <tr>
              <td className={styles.labelCell}>
                <strong>{t("category")}</strong>
              </td>
              <td className={styles.valueCell}>
                {toolPassport.panelName && toolPassport.categoryName
                  ? `${toolPassport.panelName} / ${toolPassport.categoryName}`
                  : toolPassport.categoryName || '-'}
              </td>
            </tr>

            {/* Application Area */}
            <tr>
              <td className={styles.labelCell}>
                <strong>{t("toolPassportApplicationArea")}</strong>
              </td>
              <td className={styles.valueCell}>
                {toolPassport.subcategoryName || '-'}
              </td>
            </tr>

            {/* Functions */}
            {toolPassport.function && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportFunctions")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.function)} />
                </td>
              </tr>
            )}

            {/* Abstraction Level */}
            {toolPassport.abstractionLevel && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportAbstractionLevel")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.abstractionLevel)} />
                </td>
              </tr>
            )}

            {/* I/O Connection */}
            {toolPassport.ioConnection && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportIOConnection")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.ioConnection)} />
                </td>
              </tr>
            )}

            {/* Verification Tool */}
            {toolPassport.verificationTool && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportVerificationTool")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.verificationTool)} />
                </td>
              </tr>
            )}

            {/* Application Area or Equivalence Coverage */}
            {toolPassport.applicationAreaOrEquivalenceCoverage && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportApplicationAreaOrEquivalenceCoverage")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.applicationAreaOrEquivalenceCoverage)} />
                </td>
              </tr>
            )}

            {/* Qualitative Feature */}
            {toolPassport.qualitativeFeature && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportQualitativeFeature")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.qualitativeFeature)} />
                </td>
              </tr>
            )}

            {/* Quantitative Feature */}
            {toolPassport.quantitativeFeature && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("toolPassportQuantitativeFeature")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <div dangerouslySetInnerHTML={sanitizeHTML(toolPassport.quantitativeFeature)} />
                </td>
              </tr>
            )}

            {/* URL */}
            {toolPassport.toolURL && (
              <tr>
                <td className={styles.labelCell}>
                  <strong>{t("url")}</strong>
                </td>
                <td className={styles.valueCell}>
                  <a
                    href={toolPassport.toolURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.toolLink}
                  >
                    {toolPassport.toolURL}
                  </a>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          variant="contained"
          className={styles.closeDialogButton}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ToolPassportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  toolPassport: PropTypes.shape({
    name: PropTypes.string,
    logo: PropTypes.string,
    typeOfToolName: PropTypes.string,
    panelName: PropTypes.string,
    categoryName: PropTypes.string,
    subcategoryName: PropTypes.string,
    function: PropTypes.string,
    abstractionLevel: PropTypes.string,
    ioConnection: PropTypes.string,
    verificationTool: PropTypes.string,
    applicationAreaOrEquivalenceCoverage: PropTypes.string,
    qualitativeFeature: PropTypes.string,
    quantitativeFeature: PropTypes.string,
    toolURL: PropTypes.string,
  }),
};

ToolPassportModal.defaultProps = {
  toolPassport: null,
};

export default ToolPassportModal;
